import { type Descriptor, isLegacyDescriptor } from './descriptors'
import { Types as PropControllerTypes } from '@makeswift/prop-controllers'

import {
  type BoxDisplayModel,
  type ControlInstanceArgs,
  type ControlMessage,
  type InstanceType,
  type AnyControlInstance,
  ControlInstance,
  DefaultControlInstance,
} from '@makeswift/controls'

export type PropControllerMessage = ControlMessage

export const TableFormFieldsMessageType = {
  TABLE_FORM_LAYOUT_CHANGE: 'TABLE_FORM_LAYOUT_CHANGE',
  TABLE_FORM_FIELD_LAYOUT_CHANGE: 'TABLE_FORM_FIELD_LAYOUT_CHANGE',
} as const

type TableLayoutTableFormFieldsMessage = {
  type: typeof TableFormFieldsMessageType.TABLE_FORM_LAYOUT_CHANGE
  payload: { layout: BoxDisplayModel }
}

type TableFieldLayoutTableFormFieldsMessage = {
  type: typeof TableFormFieldsMessageType.TABLE_FORM_FIELD_LAYOUT_CHANGE
  payload: { layout: BoxDisplayModel; index: number }
}

export type TableFormFieldsMessage =
  | TableLayoutTableFormFieldsMessage
  | TableFieldLayoutTableFormFieldsMessage

export class TableFormFieldsPropController extends ControlInstance<TableFormFieldsMessage> {
  recv = () => {}

  subscribe(_listener: () => void): () => void {
    // No mutable state, nothing to subscribe to
    return () => {}
  }

  isCompositeProp(): boolean {
    return false
  }

  children(): ControlInstance[] {
    return []
  }

  child(_key: string): ControlInstance | undefined {
    return undefined
  }

  resolvesToRenderableNode(): boolean {
    return false
  }

  tableFormLayoutChange(payload: { layout: BoxDisplayModel }) {
    this.sendMessage({ type: TableFormFieldsMessageType.TABLE_FORM_LAYOUT_CHANGE, payload })
  }

  tableFormFieldLayoutChange(payload: { layout: BoxDisplayModel; index: number }) {
    this.sendMessage({ type: TableFormFieldsMessageType.TABLE_FORM_FIELD_LAYOUT_CHANGE, payload })
  }
}

type DescriptorPropController<T extends Descriptor> = T extends {
  type: typeof PropControllerTypes.TableFormFields
}
  ? TableFormFieldsPropController
  : InstanceType<T>

export type DescriptorsPropControllers<T extends Record<string, Descriptor>> = {
  [K in keyof T]: undefined extends T[K]
    ? DescriptorPropController<Exclude<T[K], undefined>>
    : DescriptorPropController<T[K]>
}

export type AnyPropController = AnyControlInstance | TableFormFieldsPropController

export function createPropController({
  descriptor,
  ...args
}: {
  descriptor: Descriptor
} & ControlInstanceArgs): AnyPropController {
  if (!isLegacyDescriptor(descriptor)) {
    return descriptor.createInstance(args)
  }

  switch (descriptor.type) {
    case PropControllerTypes.TableFormFields:
      return new TableFormFieldsPropController(args)

    default:
      return new DefaultControlInstance(args)
  }
}
